<template>
  <v-main class="login-bg-main">
    <login-guestplan
      v-if="brand === 'guestplan'"
      :username.sync="username"
      :password.sync="password"
      @login="login"
      @create-account="createAccount=true"
      @reset-password="resetPassword=true"
    >
      <template #language-selector>
        <v-select
          v-model="language"
          style="max-width: 160px"
          hide-details
          dark
          color="white"
          dense
          prepend-icon="mdi-translate"
          :items="languages"
        />
      </template>
    </login-guestplan>
    <login-booq
      v-else-if="brand === 'booq'"
      :username.sync="username"
      :password.sync="password"
      @login="login"
      @reset-password="resetPassword=true"
    >
      <template #language-selector>
        <v-select
          v-model="language"
          style="max-width: 160px"
          hide-details
          dark
          color="white"
          dense
          prepend-icon="mdi-translate"
          :items="languages"
        />
      </template>
    </login-booq>

    <reset-password-dialog
      :visible="resetPassword"
      @close="resetPassword = false"
    />
    <create-account-dialog
      :visible="createAccountVisible"
      @close="createAccount = false"
    />
    <two-fa-login-dialog
      v-if="twofa"
      :twofa-method="twofaMethod"
      :other-twofa-methods="otherTwofaMethods"
      @submit="verify"
      @passkey-login="passkeyLogin"
      @switch-method="switchMethod"
      @cancel-twofa="cancelTwofa"
    />
    <confirmation-dialog
      :visible="resetPasswordHint"
      :title="$t('title.incorrect_password')"
      :message="$t('message.prompt_forgotten_password')"
      :confirm-title="$t('button.reset_password')"
      :cancel-title="$t('button.not_now')"
      @confirm="resetPasswordHintConfirmed"
      @close="resetPasswordHint = false"
    />
  </v-main>
</template>

<script lang="ts">
import Vue from 'vue';
import { performAction } from '@/services/vue-utils';
import { supportedLanguages } from '@/plugins/i18n';
import ResetPasswordDialog from '@/components/views/login/ResetPasswordDialog.vue';
import CreateAccountDialog from '@/components/views/login/CreateAccountDialog.vue';
import storage from '@/services/local-storage';
import { ApiErrorCode } from '@/api/api-error';
import httpClient from '@/api/http-client';
import { setDarkAllowed } from '@/services/autodarkmode';
import setLanguage from '@/plugins/i18n-locale';
import { defaultHomePath } from '@/composables/account-switcher';

export default Vue.extend({
  name: 'Login',
  components: {
    ResetPasswordDialog,
    CreateAccountDialog,
    TwoFaLoginDialog: () => import('@/components/views/login/TwoFALoginDialog.vue'),
    LoginGuestplan: () => import('@/components/views/login/LoginGuestplan.vue'),
    LoginBooq: () => import('@/components/views/login/LoginBooq.vue'),
  },
  props: {},
  data() {
    return {
      languages: supportedLanguages,
      language: this.$i18n.locale.split('-')[0],
      username: import.meta.env.VITE_DEFAULT_USERNAME ?? null,
      password: import.meta.env.VITE_DEFAULT_PASSWORD ?? null,
      failedAttempts: 0,
      resetPassword: false,
      createAccount: false,
      resetPasswordHint: false,
      twofa: false,
      twofaSession: null as string | null,
      twofaMethod: null as string | null,
      twofaAllowedMethods: [] as string[],
      twofaEffectiveMethods: [] as string[],
    };
  },
  computed: {
    brand(): string {
      return import.meta.env.VITE_BRAND || 'guestplan';
    },
    isRegistered(): boolean {
      return this.$tstore.getters.isRegistered;
    },
    isLoggedIn(): boolean {
      return this.$tstore.getters.isLoggedIn;
    },
    isSignup(): boolean {
      return this.$route.name === 'signup' || this.$route.name === 'promo';
    },
    createAccountVisible(): boolean {
      return this.createAccount || this.isLoggedIn;
    },
    otherTwofaMethods(): string[] {
      const pool = this.twofaEffectiveMethods.length > 0
        ? this.twofaEffectiveMethods
        : this.twofaAllowedMethods;
      return pool.filter((m) => m && m !== this.twofaMethod);
    },
  },
  watch: {
    language() {
      setLanguage(this.language, '');
    },
    username() {
      this.failedAttempts = 0;
    },
    isRegistered(newValue: boolean) {
      // registration changed while on login screen -> go to home route
      if (!newValue) return;
      if (this.$route.meta?.public !== true) return;

      this.$router.push(defaultHomePath());
      storage.setLanguage(this.$i18n.locale);
      setDarkAllowed(true);
    },

  },
  async mounted() {
    setDarkAllowed(false);
    if (this.isSignup) {
      this.createAccount = true;
    }
    if (typeof this.$route.query.username === 'string' && this.$route.query.username !== '') {
      this.username = this.$route.query.username;
    }
  },
  methods: {
    async login() {
      let ok = true;
      let twofaResult: any = null;
      try {
        await performAction(
          null,
          this.$i18n.tc('error.login'),
          async () => {
            twofaResult = await this.$tstore.dispatch('login', { username: this.username, password: this.password });
          },
          false,
          true,
        );

        if (twofaResult?.twofa) {
          this.twofa = true;
          this.twofaSession = twofaResult.session;
          this.twofaMethod = twofaResult.method;
          this.twofaAllowedMethods = twofaResult.allowedMethods || [];
          this.twofaEffectiveMethods = twofaResult.effectiveMethods || [];
          if (twofaResult.method === 'passkey') {
            await this.$nextTick();
            await this.passkeyLogin();
          }
          return;
        }
      } catch (e: any) {
        ok = false;
        if (e.code === ApiErrorCode.user_not_found) {
          this.failedAttempts += 1;
          this.resetPasswordHint = (this.failedAttempts % 3) === 0;
        }
      }

      if (ok) {
        this.failedAttempts = 0;

        // const route = twofaResult?.twofaSetup ? '/settings/users' : '/';
        // this.$router.push(route);
        this.$router.push(defaultHomePath());
        storage.setLanguage(this.$i18n.locale);
        setDarkAllowed(true);
      }
    },
    async verify(code: string) {
      let ok = true;
      try {
        await performAction(
          this.$i18n.tc('message.success_sign_in'),
          this.$i18n.tc('error.login'),
          async () => this.$tstore.dispatch('login', {
            username: this.username,
            password: this.password,
            session: this.twofaSession,
            code,
          }),
          false,
          true,
        );
      } catch (e: any) {
        ok = false;
      }

      if (ok) {
        this.cancelTwofa();
        this.failedAttempts = 0;
        this.$router.push(defaultHomePath());
        storage.setLanguage(this.$i18n.locale);
        setDarkAllowed(true);
      }
    },
    async passkeyLogin() {
      if (!this.twofaSession || !this.password) return;
      let ok = true;
      try {
        await performAction(
          this.$i18n.tc('message.success_sign_in'),
          this.$i18n.tc('error.login'),
          async () => {
            await httpClient.loginPasskey(this.twofaSession!, this.password!);
            this.$tstore.commit('SET_USERNAME', this.username);
          },
          false,
          true,
        );
      } catch {
        ok = false;
      }
      if (ok) {
        this.cancelTwofa();
        this.failedAttempts = 0;
        this.$router.push(defaultHomePath());
        storage.setLanguage(this.$i18n.locale);
        setDarkAllowed(true);
      }
    },
    async switchMethod(method: string) {
      try {
        const result = await this.$tstore.dispatch('login', {
          username: this.username,
          password: this.password,
          session: this.twofaSession,
          method,
        });
        if (result?.twofa) {
          this.twofaSession = result.session;
          this.twofaMethod = result.method;
          this.twofaAllowedMethods = result.allowedMethods || [];
          this.twofaEffectiveMethods = result.effectiveMethods || [];
          if (method === 'passkey') {
            await this.$nextTick();
            await this.passkeyLogin();
          }
        }
      } catch {
        // stay on current method if switch fails
      }
    },
    cancelTwofa() {
      this.twofa = false;
      this.twofaSession = null;
      this.twofaMethod = null;
      this.twofaAllowedMethods = [];
      this.twofaEffectiveMethods = [];
    },
    resetPasswordHintConfirmed() {
      this.resetPassword = true;
    },
  },
});
</script>
